// Data for the new sections: Garde-meubles, Location de matériel, Calculateur de volume
import {
  Warehouse, ShieldCheck, Clock, Thermometer, Camera, KeyRound,
  Package, Truck, Sofa, Boxes, Wrench, ArrowUpDown,
} from "lucide-react";

export const gardeMeubles = {
  hero: {
    title: "Garde-meubles & stockage sécurisé",
    subtitle: "Des espaces de stockage propres, surveillés et accessibles pour vos biens, en courte ou longue durée.",
    image: "https://images.unsplash.com/photo-1684695749267-233af13276d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxzdG9yYWdlJTIwd2FyZWhvdXNlfGVufDB8fHx8MTc4MzQzMDIzMHww&ixlib=rb-4.1.0&q=85",
  },
  intro: [
    "Un imprévu, une transition entre deux logements ou simplement un besoin d'espace ? DMP Déménagement met à votre disposition des box de stockage sécurisés adaptés à tous les volumes.",
    "Nos entrepôts sont surveillés 24h/24, protégés contre l'humidité et accessibles sur rendez-vous. Nous pouvons également récupérer et livrer vos biens grâce à notre service de transport intégré.",
  ],
  features: [
    { icon: "ShieldCheck", title: "Sécurité 24h/24", desc: "Vidéosurveillance, alarme et accès contrôlé pour une tranquillité totale." },
    { icon: "Thermometer", title: "Espaces sains", desc: "Locaux ventilés et protégés contre l'humidité pour préserver vos biens." },
    { icon: "Clock", title: "Durée flexible", desc: "Stockage à la semaine, au mois ou à l'année, sans engagement long." },
    { icon: "Truck", title: "Enlèvement & livraison", desc: "Nous transportons vos biens vers et depuis le garde-meuble." },
    { icon: "KeyRound", title: "Accès dédié", desc: "Un accès personnel à votre box sur simple rendez-vous." },
    { icon: "Camera", title: "Inventaire photo", desc: "État des lieux et inventaire de vos biens à l'entrée en stockage." },
  ],
  boxes: [
    { size: "3 m³", label: "Petit box", desc: "Idéal pour quelques cartons et petits meubles.", price: "dès 29€/mois" },
    { size: "8 m³", label: "Box studio", desc: "Le contenu d'un studio ou d'un petit appartement.", price: "dès 59€/mois", popular: true },
    { size: "15 m³", label: "Box appartement", desc: "Pour un T2/T3 meublé complet.", price: "dès 99€/mois" },
    { size: "30 m³+", label: "Box maison", desc: "Le mobilier d'une maison entière.", price: "sur devis" },
  ],
  image: "https://images.unsplash.com/photo-1551313158-73d016a829ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxzdG9yYWdlJTIwdW5pdHN8ZW58MHx8fHwxNzgzNDMwMjMwfDA&ixlib=rb-4.1.0&q=85",
};

export const locationMateriel = {
  hero: {
    title: "Location de matériel de déménagement",
    subtitle: "Cartons, diables, sangles, couvertures... Louez tout le matériel professionnel pour un déménagement en toute autonomie.",
    image: "https://images.unsplash.com/photo-1556216607-691819089c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxoYW5kJTIwdHJ1Y2t8ZW58MHx8fHwxNzgzNDMwMjM5fDA&ixlib=rb-4.1.0&q=85",
  },
  intro: [
    "Vous préférez déménager par vous-même ? Nous mettons à votre disposition du matériel professionnel de qualité, à la location, pour protéger et transporter vos biens en toute sécurité.",
    "Réservez votre matériel à l'avance, récupérez-le en agence ou faites-vous livrer. Caution simple et tarifs dégressifs selon la durée.",
  ],
  items: [
    { icon: "Boxes", name: "Cartons de déménagement", desc: "Cartons standards et renforcés (livres, penderie).", price: "dès 1,50€/pièce" },
    { icon: "Truck", name: "Diable / chariot", desc: "Diable pliant et chariot à roulettes robustes.", price: "dès 12€/jour" },
    { icon: "ArrowUpDown", name: "Sangles de portage", desc: "Sangles ergonomiques pour porter meubles lourds.", price: "dès 6€/jour" },
    { icon: "Sofa", name: "Couvertures matelassées", desc: "Protection des meubles et objets fragiles.", price: "dès 4€/jour" },
    { icon: "Package", name: "Film & papier bulle", desc: "Film étirable, papier bulle et adhésif.", price: "dès 5€/rouleau" },
    { icon: "Wrench", name: "Kit outillage", desc: "Outils pour démontage / remontage de meubles.", price: "dès 9€/jour" },
  ],
};

export const transportObjet = {
  hero: {
    title: "Transport d'objets lourds & objets d'art",
    subtitle: "Un service spécialisé pour vos biens les plus lourds et les plus précieux, avec équipements dédiés et manutention experte.",
    image: "https://images.pexels.com/photos/7464676/pexels-photo-7464676.jpeg",
  },
  intro: [
    "Certains objets exigent un savoir-faire particulier : un piano, un coffre-fort, une œuvre d'art ou une antiquité de valeur ne se transportent pas comme un simple carton.",
    "DMP Déménagement met à votre disposition une équipe formée, des équipements spécialisés (monte-meubles, sangles, caisses sur mesure) et une assurance renforcée pour un transport en toute sécurité.",
  ],
  lourds: {
    desc: "Manutention de charges lourdes et volumineuses avec matériel adapté et personnel formé aux techniques de portage sécurisé.",
    image: "https://images.pexels.com/photos/7464722/pexels-photo-7464722.jpeg",
    items: ["Pianos droits & à queue", "Coffres-forts", "Machines & équipements", "Spas & jacuzzis", "Billards & flippers", "Mobilier massif"],
  },
  art: {
    desc: "Emballage muséal et transport ultra-sécurisé de vos œuvres et objets de valeur, avec le plus grand soin.",
    image: "https://images.pexels.com/photos/33152693/pexels-photo-33152693.jpeg",
    items: ["Tableaux & cadres", "Sculptures", "Antiquités", "Objets fragiles de collection", "Miroirs & vitraux", "Œuvres de grande valeur"],
  },
  guarantees: [
    { icon: "ShieldCheck", title: "Assurance renforcée", desc: "Couverture spécifique adaptée à la valeur de vos objets." },
    { icon: "Boxes", title: "Emballage sur mesure", desc: "Caisses et protections fabriquées selon l'objet à transporter." },
    { icon: "Users", title: "Équipe spécialisée", desc: "Déménageurs formés aux techniques de portage et manutention." },
    { icon: "ArrowUpDown", title: "Monte-meubles", desc: "Équipements pour accès difficiles et étages élevés." },
  ],
};

// Volume calculator catalogue (average volume in m³ per item)
export const volumeCatalogue = [
  {
    category: "Salon",
    items: [
      { name: "Canapé 2 places", volume: 1.2 },
      { name: "Canapé 3 places", volume: 1.8 },
      { name: "Fauteuil", volume: 0.7 },
      { name: "Table basse", volume: 0.5 },
      { name: "Meuble TV", volume: 0.8 },
      { name: "Bibliothèque", volume: 1.0 },
      { name: "Téléviseur", volume: 0.3 },
    ],
  },
  {
    category: "Chambre",
    items: [
      { name: "Lit simple", volume: 1.0 },
      { name: "Lit double", volume: 1.6 },
      { name: "Armoire 2 portes", volume: 1.5 },
      { name: "Commode", volume: 0.7 },
      { name: "Table de chevet", volume: 0.2 },
      { name: "Bureau", volume: 0.9 },
    ],
  },
  {
    category: "Cuisine",
    items: [
      { name: "Réfrigérateur", volume: 0.8 },
      { name: "Lave-linge", volume: 0.6 },
      { name: "Lave-vaisselle", volume: 0.6 },
      { name: "Table à manger", volume: 1.0 },
      { name: "Chaise", volume: 0.25 },
      { name: "Four / micro-ondes", volume: 0.2 },
    ],
  },
  {
    category: "Divers",
    items: [
      { name: "Carton standard", volume: 0.1 },
      { name: "Carton penderie", volume: 0.3 },
      { name: "Vélo", volume: 0.5 },
      { name: "Machine de sport", volume: 1.2 },
      { name: "Piano droit", volume: 2.0 },
      { name: "Carton livres", volume: 0.08 },
    ],
  },
];
